/**
 * ResuspensionPenaltyPage — Bryant 2011 SOD-increase warning
 *
 * Wrongly placed modules can INCREASE sediment oxygen demand by 20–30%
 * because plume bed-shear uncovers organic-rich layers below a thin oxidised
 * surface crust. When enabled, validation flags this risk.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AlertOctagon, ArrowLeft, Info, AlertTriangle, CheckCircle2 } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { specFromLegacy, type LegacyModSpec, plumeBedVelocityMs, bedShearPa } from '@/lib/diffuserPhysics';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

interface ModSpec extends LegacyModSpec { waterDepthM: number; mountingHeightM: number; waterTempC: number; }
const MOD_DEFAULTS: ModSpec = { diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300, poreSizeUm: 80, airflowPerDiffNm3h: 1.2, waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12 };

// Bryant 2011: SOD-uncovering threshold for organic-rich sub-surface sediment.
// Plume bed-shear above this value typically increases bulk SOD by 20–30%.
const TAU_UNCOVER_PA: Record<string, number> = {
  clay: 0.9,         // strong cohesion → high threshold
  fine_silt: 0.4,
  medium_silt: 0.3,
  fine_sand: 0.2,
};

export default function ResuspensionPenaltyPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('resusp_penalty');
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }));
  const [bed, setBed] = useState<keyof typeof TAU_UNCOVER_PA>('medium_silt');
  const [organicLayer_mm, setOrganicLayer] = useState(8);

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) });
    });
    return () => unsub();
  }, []);

  const result = useMemo(() => {
    const dSpec = specFromLegacy(spec);
    const H_eff = Math.max(0.3, spec.waterDepthM - spec.mountingHeightM);
    const U_bed = plumeBedVelocityMs(dSpec, H_eff);
    const tau_bed = bedShearPa(dSpec, H_eff, spec.waterTempC);
    const tau_uncover = TAU_UNCOVER_PA[bed];
    const ratio = tau_bed / tau_uncover;
    const willUncover = ratio > 1;
    const sod_uplift_pct = willUncover ? Math.min(35, 20 * ratio - 10) : 0;
    return { U_bed, tau_bed, tau_uncover, ratio, willUncover, sod_uplift_pct };
  }, [spec, bed]);

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={AlertOctagon}
        title="Module — Resuspension penalty flag"
        subtitle="Bryant 2011: detect modules placed over organic-rich sub-surface sediment"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${
              enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'
            }`}>
              <input type="checkbox" checked={enabled} onChange={() => toggle('resusp_penalty')} className="accent-emerald-500" />
              Module {enabled ? 'ENABLED' : 'DISABLED'}
            </label>
            <button onClick={() => navigate('/modules')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Modules
            </button>
          </div>
        }
      />

      <ModuleConflictBanner moduleId="resusp_penalty" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Bed shear τ</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{result.tau_bed.toFixed(3)}</div>
          <div className="text-xs text-slate-500 mt-1">Pa</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Uncovering threshold</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{result.tau_uncover.toFixed(2)}</div>
          <div className="text-xs text-slate-500 mt-1">Pa (Bryant 2011)</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Ratio τ / τ_uncover</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{result.ratio.toFixed(2)}</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${result.willUncover ? 'border-red-700 bg-red-900/15' : 'border-emerald-700 bg-emerald-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-400">Risk</div>
          <div className="text-2xl font-bold text-slate-100 mt-1 flex items-center gap-1.5">
            {result.willUncover ? <AlertTriangle className="w-6 h-6 text-red-400" /> : <CheckCircle2 className="w-6 h-6 text-emerald-400" />}
            {result.willUncover ? 'UNCOVERING' : 'SAFE'}
          </div>
          {result.willUncover && (
            <div className="text-xs text-red-300 mt-1">+{result.sod_uplift_pct.toFixed(0)}% SOD uplift predicted</div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Bed type at module location</label>
            <select value={bed} onChange={e => setBed(e.target.value as keyof typeof TAU_UNCOVER_PA)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100">
              <option value="clay">Clay (cohesive, τ_uncover = 0.9 Pa)</option>
              <option value="fine_silt">Fine silt (τ_uncover = 0.4 Pa)</option>
              <option value="medium_silt">Medium silt (τ_uncover = 0.3 Pa)</option>
              <option value="fine_sand">Fine sand (τ_uncover = 0.2 Pa)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Oxidised surface layer thickness (mm)</label>
            <input type="number" step={0.5} value={organicLayer_mm}
              onChange={e => setOrganicLayer(Number(e.target.value))}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> What this module does when enabled
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            Bryant et al. 2011 demonstrated a <strong className="text-red-300">counter-intuitive failure
            mode</strong>: wrongly placed aeration modules can <em>increase</em> sediment oxygen demand by
            20–30 %. The mechanism is plume-induced bed shear uncovering organic-rich layers that sit
            below a thin oxidised surface crust. The newly exposed material is highly oxygen-hungry and
            consumes the AOTR before it can reach the water column.
          </p>
          <p>
            <strong className="text-emerald-300">Downstream impact when enabled:</strong> A new validation
            step appears at L6 Sediment in the Validation Steps page, flagging FAIL when plume bed-shear
            exceeds the type-specific uncovering threshold. The Bed Assessment page shows a penalty
            warning on its SOD section. The recommended mitigation: raise the module mounting height,
            reduce per-diffuser airflow, or relocate the module to a less organic-rich zone.
          </p>
          <p>
            <strong className="text-amber-300">Current evaluation:</strong> With {bed.replace('_', ' ')} bed
            and the active session, plume bed-shear is <strong>{result.tau_bed.toFixed(3)} Pa</strong> vs
            uncovering threshold <strong>{result.tau_uncover.toFixed(2)} Pa</strong> — ratio <strong>{result.ratio.toFixed(2)}×</strong>.
            {result.willUncover ? ' Risk of SOD uplift detected.' : ' Risk acceptable.'}
          </p>
          <p className="italic text-slate-500">References: Bryant, Gantzer & Little 2011; Mehta 1986.</p>
        </div>
      </div>
    </div>
  );
}
